import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router";
import { useApp } from "../components/appState";
import { MON, plural } from "../lib/format";
import { fetchStats, heat, heatmap, pct, ticks, yearSeries, type Ranked, type StatsDto } from "../lib/statsPage";

function Bars({ data, onYear }: { data: StatsDto["by_year"]; onYear: (y: number) => void }) {
  const series = yearSeries(data);
  if (!series.length) return null;
  const W = 640, H = 190, pl = 26, pb = 20, pt = 8;
  const max = Math.max(...series.map((s) => s.count));
  const grid = ticks(max);
  const top = grid[grid.length - 1];
  const bw = (W - pl) / series.length;
  const sy = (v: number) => H - pb - (v / top) * (H - pb - pt);
  const peak = series.find((s) => s.count === max)?.year;
  return (
    <svg className="bars" viewBox={`0 0 ${W} ${H}`} role="img" aria-label={`Shows per year, ${series[0].year} to ${series[series.length - 1].year}`}>
      {grid.map((t) => (
        <g key={t}>
          <line className="gl" x1={pl} x2={W} y1={sy(t)} y2={sy(t)} />
          <text x={pl - 6} y={sy(t) + 3} textAnchor="end">
            {t}
          </text>
        </g>
      ))}
      {series.map((s, i) => {
        const x = pl + i * bw + 2;
        return (
          <g key={s.year}>
            <rect x={x} y={sy(s.count)} width={Math.max(bw - 4, 1)} height={Math.max(H - pb - sy(s.count), 1)} rx={2}
              fill={s.year === peak ? "var(--accent)" : "#CFC8B8"} onClick={() => s.count && onYear(s.year)}>
              <title>{`${s.year}: ${plural(s.count, "show")}`}</title>
            </rect>
            {i % 2 === 0 && (
              <text x={x + (bw - 4) / 2} y={H - 5} textAnchor="middle">
                '{String(s.year).slice(2)}
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
}

function Top({ title, rows, kind, n, onToggle }: { title: string; rows: Ranked[]; kind: "band" | "venue"; n: number; onToggle: () => void }) {
  const { open } = useApp();
  return (
    <section>
      <div className="sect-h">
        <p className="eyebrow">{title}</p>
        <button type="button" className="meta link" onClick={onToggle}>
          {n === 5 ? "Top 20" : "Top 5"}
        </button>
      </div>
      <div className="facts">
        {rows.slice(0, n).map((r, i) => (
          <button type="button" key={r.id} onClick={() => open(kind, r.id)}>
            <span className="fk">
              {i + 1}. {r.name}
            </span>
            <span className="fv2 mono">{r.count}</span>
          </button>
        ))}
      </div>
    </section>
  );
}

export default function Stats() {
  const { open, version } = useApp();
  const navigate = useNavigate();
  const [data, setData] = useState<StatsDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [topN, setTopN] = useState(5);

  useEffect(() => {
    let live = true;
    fetchStats()
      .then((d) => live && setData(d))
      .catch((e: Error) => live && setError(e.message));
    return () => {
      live = false;
    };
  }, [version]);

  if (error) return <p className="error">{error}</p>;
  if (!data) return <p className="loading">Loading stats…</p>;
  const t = data.totals;
  const hm = heatmap(data.by_month);
  const toggle = () => setTopN((n) => (n === 5 ? 20 : 5));
  return (
    <>
      <div className="ph">
        <h1>Stats</h1>
        {t.first && t.last && (
          <span className="meta mono">
            {t.first.slice(0, 4)}–{t.last.slice(0, 4)}
          </span>
        )}
      </div>
      <div className="totals">
        <div className="tot"><b>{t.shows}</b><span>shows</span></div>
        <div className="tot"><b>{t.bands}</b><span>bands</span></div>
        <div className="tot"><b>{t.venues}</b><span>venues</span></div>
        <div className="tot"><b>{pct(t.with_flyer, t.shows)}%</b><span>have a flyer</span></div>
      </div>
      <section>
        <div className="sect-h">
          <p className="eyebrow">Shows by year</p>
          <span className="meta">tap a year</span>
        </div>
        <Bars data={data.by_year} onYear={(y) => open("year", y)} />
      </section>
      <section>
        <div className="sect-h">
          <p className="eyebrow">By month</p>
          <span className="meta">darker = more shows</span>
        </div>
        <div className="hm">
          <div className="r hdr-r2">
            <span />
            {"JFMAMJJASOND".split("").map((m, i) => (
              <span key={i}>{m}</span>
            ))}
          </div>
          {hm.years.map((y) => (
            <div className="r" key={y}>
              <span>{y}</span>
              {MON.map((mo, m) => {
                const c = hm.count(y, m + 1);
                const h = heat(c, hm.max);
                return (
                  <span key={mo} className="c" title={`${mo} ${y}: ${c}`}
                    style={{ background: h ? `color-mix(in srgb, var(--accent) ${h}%, var(--surface))` : "var(--line-soft)" }} />
                );
              })}
            </div>
          ))}
        </div>
      </section>
      <div className="split">
        <Top title="Most seen bands" rows={data.top_bands} kind="band" n={topN} onToggle={toggle} />
        <Top title="Most visited venues" rows={data.top_venues} kind="venue" n={topN} onToggle={toggle} />
      </div>
      {data.events.length > 0 && (
        <section>
          <div className="sect-h">
            <p className="eyebrow">Events</p>
            <span className="meta">tap one for its shows</span>
          </div>
          <div className="facts">
            {data.events.map((e) => (
              <button type="button" key={e.id} onClick={() => navigate(`/?q=${encodeURIComponent(e.name)}`)}>
                <span className="fk">{e.name}</span>
                <span className="fv2 mono">{plural(e.count, "show")}</span>
              </button>
            ))}
          </div>
        </section>
      )}
      {/* People is kept out of the header and the lists (Matt, 2026-09-11); this is the way in. */}
      <div className="facts">
        <Link className="fact" to="/people">
          <span className="fk">People you've gone with</span>
          <span className="fv2">›</span>
        </Link>
      </div>
    </>
  );
}
